import { Link } from 'gatsby';
import { ArrowRight, GitMerge, Target, Zap } from 'lucide-react';
import React from 'react';
import styled from '../../styled';
import { Headline } from '../Headline';
import { mobileHeroLeadText } from '../MarketingTypography';
import { Reveal } from '../motion';
import { NewBadge } from '../NewBadge';
import { ProductIcon } from '../ProductIcon';
import { CommerceLoopPanel } from './CommerceLoopPanel';

const SectionWrapper = styled('section')`
  background: linear-gradient(
    180deg,
    #fff 0%,
    #f4f8fc 12%,
    #f4f8fc 88%,
    #fff 100%
  );
  background-color: #f4f8fc;
  position: relative;
  padding: 100px 0 80px;

  /* Subtle dot grid, faded at the edges */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      rgba(14, 28, 43, 0.07) 1px,
      transparent 1px
    );
    background-size: 22px 22px;
    mask-image: radial-gradient(
      ellipse 80% 70% at 50% 45%,
      black 30%,
      transparent 100%
    );
    pointer-events: none;
  }

  > * {
    position: relative;
  }

  @media (max-width: 960px) {
    padding: 72px 0 56px;
  }
`;

const SectionHeader = styled('div')`
  text-align: center;
  margin-bottom: 16px;
  padding: 0 24px;
`;

const SectionDescription = styled('p')`
  font-size: 18px;
  line-height: 1.6;
  color: #4f4f4f;
  margin-top: 16px;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
  ${mobileHeroLeadText}
`;

const SubsectionHeader = styled('div')`
  text-align: center;
  margin-top: 40px;
  padding: 0 24px;
`;

const LinkCards = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  max-width: 1000px;
  margin: 32px auto 0;
  padding: 0 24px;

  /* Stretch the reveal wrappers so all cards share the same height */
  > * {
    height: 100%;
    min-width: 0;
  }

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 0 16px;
  }
`;

const LinkCard = styled(Link)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  box-sizing: border-box;
  padding: 24px 22px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  text-decoration: none;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
  transition: border-color 0.25s ease, box-shadow 0.25s ease,
    transform 0.25s ease;

  &:hover {
    border-color: #2e5cff;
    box-shadow: 0 8px 30px rgba(46, 92, 255, 0.12);
    transform: translateY(-2px);
  }

  h3 {
    font-size: 19px;
    font-weight: 700;
    color: #0e1c2b;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  p {
    font-size: 14px;
    line-height: 1.55;
    color: #64748b;
    margin: 0;
  }
`;

const PerfectForLabel = styled('p')`
  font-size: 13px;
  font-weight: 600;
  color: #4f4f4f;
  margin: 4px 0 0;
`;

const TeamList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
`;

const TeamItem = styled('li')`
  font-size: 13px;
  line-height: 1.45;
  color: #64748b;
  position: relative;
  padding-left: 16px;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #2e5cff;
    font-weight: bold;
  }
`;

const CardHeader = styled('div')`
  display: flex;
  align-items: center;
`;

const CardLink = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #2e5cff;
`;

const CardFooter = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
  margin-top: auto;
`;

const UNIFY_TEAM_BULLETS = [
  'One place to review and act on your affiliate performance',
  'Centralized linking via extension or API',
  'Easy AI tool integration through MCP',
  'Confident reconciliation and month-end closing for finance',
  'Modern APIs and data warehouse compatibility trusted by BI teams'
] as const;

const OPTIMIZE_TEAM_BULLETS = [
  'Conversions by page URL and link, visualized in editor-friendly heatmaps',
  'Revenue by traffic source, offsite channels, content segmentation, LLM referrals, and AI agent crawls',
  'Audience segments powered by buying and subscriber behavior',
  'Growth without operational overhead'
] as const;

const AMPLIFY_TEAM_BULLETS = [
  'Campaign infrastructure that supports scale, with live insights to adjust mid-campaign',
  'AI-powered, portfolio-wide recommendations you control and can see into, not a black box',
  'Automated link swaps, not manual edits',
  'New revenue without new inventory'
] as const;

export const CommerceLoopSection = () => {
  return (
    <SectionWrapper id="publisher-solutions">
      <SectionHeader>
        <Reveal>
          <Headline component="h2">Publisher Solutions</Headline>
          <SectionDescription>
            Affilimate turns your links, pages, and placements into unified
            commerce data, then gives your team the tools to grow revenue with
            it.
          </SectionDescription>
        </Reveal>
      </SectionHeader>

      <CommerceLoopPanel />

      <SubsectionHeader>
        <Reveal>
          <Headline component="h2">
            Grow through every stage of commerce.
          </Headline>
          <SectionDescription>
            Use one product or combine them as you grow.
          </SectionDescription>
        </Reveal>
      </SubsectionHeader>

      <LinkCards>
        <Reveal>
          <LinkCard to="/product/unify/">
            <CardHeader>
              <ProductIcon>
                <GitMerge size={20} />
              </ProductIcon>
            </CardHeader>
            <h3>Unify</h3>
            <p>
              Your commerce data foundation. Transactions, partners, and rates
              from 100+ networks, normalized and available via an easy-to-use
              interface, scalable API, MCP, and data warehouse connectors.
            </p>
            <PerfectForLabel>Built for teams who want:</PerfectForLabel>
            <TeamList>
              {UNIFY_TEAM_BULLETS.map((item) => (
                <TeamItem key={item}>{item}</TeamItem>
              ))}
            </TeamList>
            <CardFooter>
              <CardLink>
                Learn more <ArrowRight size={16} />
              </CardLink>
            </CardFooter>
          </LinkCard>
        </Reveal>

        <Reveal delay={80}>
          <LinkCard to="/product/optimize/">
            <CardHeader>
              <ProductIcon>
                <Target size={20} />
              </ProductIcon>
            </CardHeader>
            <h3>Optimize</h3>
            <p>
              Earn more from existing content and drive your content strategy.
              Tie clicks and revenue to articles, traffic sources, audiences,
              and offsite distribution. Expose exactly where revenue is won,
              lost, and ready to scale.
            </p>
            <PerfectForLabel>Built for teams who want:</PerfectForLabel>
            <TeamList>
              {OPTIMIZE_TEAM_BULLETS.map((item) => (
                <TeamItem key={item}>{item}</TeamItem>
              ))}
            </TeamList>
            <CardFooter>
              <CardLink>
                Learn more <ArrowRight size={16} />
              </CardLink>
            </CardFooter>
          </LinkCard>
        </Reveal>

        <Reveal delay={160}>
          <LinkCard to="/product/amplify/">
            <CardHeader>
              <ProductIcon>
                <Zap size={20} />
              </ProductIcon>
            </CardHeader>
            <h3>
              Amplify
              <NewBadge>New</NewBadge>
            </h3>
            <p>
              Sell and prove more sponsorship campaigns. Plan deals, track
              delivery in flight, and report results your clients trust. Then
              scale your available inventory with AI-powered, portfolio-wide
              link swaps you can sell to brands.
            </p>
            <PerfectForLabel>Built for teams who want:</PerfectForLabel>
            <TeamList>
              {AMPLIFY_TEAM_BULLETS.map((item) => (
                <TeamItem key={item}>{item}</TeamItem>
              ))}
            </TeamList>
            <CardFooter>
              <CardLink>
                Learn more <ArrowRight size={16} />
              </CardLink>
            </CardFooter>
          </LinkCard>
        </Reveal>
      </LinkCards>
    </SectionWrapper>
  );
};
